import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { AppliedJobsAction } from "../../../Redux/action/AppliedJobsAction";
import Pagination from "../../Common/Pagination/Pagination";
import { TbExternalLink } from "react-icons/tb";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Divider,
  Skeleton,
} from "@mui/material";
import { CandidateProfile } from "../../../Redux/action/CandidateProfileAction";

const PRIMARY = "#286a94";

const ApplicationStatus = () => {
  const { userResult } = useSelector((state) => state.Auth);
  const [currentPage, setcurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);

  const dispatch = useDispatch();
  const candidateId = userResult?.userid;

  const userInfo = useSelector((state) => state?.AppliedJobs?.result?.content);
  const totalPages = useSelector(
    (state) => state?.AppliedJobs?.result?.totalPages
  );
  const loading = useSelector((state) => state?.AppliedJobs?.loading);

  useEffect(() => {
    dispatch(AppliedJobsAction({ candidateId, pageNumber, pageSize: 10 }));
  }, [pageNumber]);

  useEffect(()=>{
    dispatch(CandidateProfile(candidateId))
  },[dispatch]);

  const handlePageChange = (page) => setPageNumber(page);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = userInfo?.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", mt: 2, px: { xs: 1, sm: 2 } }}>
      {/* ---- Heading ---- */}
      <Typography variant="h5" fontWeight={700} sx={{ color: PRIMARY }}>
        📄 Application Status
      </Typography>

      <Typography sx={{ fontSize: "14px", color: "#6b7280", mt: 1 }}>
        Track your job applications and stay updated with the hiring progress.
      </Typography>

      <Paper
        elevation={3}
        sx={{
          mt: 4,
          borderRadius: "14px",
          overflow: "hidden",
          border: "1px solid #e5e7eb",
        }}
      >
        {/* ✅ Responsive wrapper */}
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead sx={{ background: PRIMARY + "15" }}>
              <TableRow>
                <TableCell align="center">
                  <strong>#</strong>
                </TableCell>

                <TableCell>
                  <strong>Job ID</strong>
                </TableCell>

                {/* Hide on mobile */}
                <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                  <strong>Company ID</strong>
                </TableCell>

                <TableCell>
                  <strong>Designation</strong>
                </TableCell>

                <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                  <strong>Applied Date</strong>
                </TableCell>

                <TableCell>
                  <strong>Status</strong>
                </TableCell>

                <TableCell align="center">
                  <strong>Action</strong>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell align="center">
                      <Skeleton width={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={100} />
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      <Skeleton width={100} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width="70%" />
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", sm: "table-cell" } }}
                    >
                      <Skeleton width={80} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={120} />
                    </TableCell>
                    <TableCell align="center">
                      <Skeleton width={30} />
                    </TableCell>
                  </TableRow>
                ))
              ) : currentItems?.length > 0 ? (
                currentItems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell align="center">
                      {pageNumber === 0 ? index + 1 : index + (pageNumber + 10)}
                    </TableCell>

                    <TableCell>{item.jobUuid}</TableCell>

                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {item.vendorEmpId}
                    </TableCell>

                    <TableCell>{item.jobTitle}</TableCell>

                    <TableCell
                      sx={{ display: { xs: "none", sm: "table-cell" } }}
                    >
                      {item.createdAt
                        ? new Date(item.createdAt)
                            .toLocaleDateString("en-GB")
                            .replace(/\//g, "-")
                        : "N/A"}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={
                          item.jobAcceptanceStatus === "INTERVIEW_SCHEDULED"
                            ? "Interview Scheduled"
                            : item.jobAcceptanceStatus === "PENDING"
                            ? "In Progress"
                            : item.jobAcceptanceStatus === "NOT_SHORTLISTED"
                            ? "Not Shortlisted"
                            : "Rejected"
                        }
                        sx={{
                          background:
                            item.jobAcceptanceStatus === "INTERVIEW_SCHEDULED"
                              ? "#DCFCE7"
                              : item.jobAcceptanceStatus === "PENDING"
                              ? "#FEF9C3"
                              : "#FEE2E2",
                          color:
                            item.jobAcceptanceStatus === "INTERVIEW_SCHEDULED"
                              ? "#166534"
                              : item.jobAcceptanceStatus === "PENDING"
                              ? "#92400E"
                              : "#991B1B",
                          fontWeight: 600,
                          fontSize: "12px",
                        }}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Link
                        to={`/view_job/${item.jobId}?fromApplicationStatus=true`}
                      >
                        <TbExternalLink
                          size={20}
                          color={PRIMARY}
                          style={{ cursor: "pointer" }}
                        />
                      </Link>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography>No Data Found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {/* Pagination */}
      {totalPages > 0 && (
        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
          <Pagination
            totalPages={totalPages}
            currentPage={pageNumber}
            onPageChange={handlePageChange}
          />
        </Box>
      )}
    </Box>
  );
};

export default ApplicationStatus;
